import { useTranslations } from '@vocab/react';
import { parseISO, isBefore, add } from 'date-fns';

import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';
import { TrackOnMount } from 'src/libs/Analytics';

import translations from './.vocab';
import { FreemiumModal } from './FreemiumModalBase';
import { FreemiumModalContent } from './FreemiumModalContent';

const shouldShowUrgentHire = ({
  jobCreatedDate,
}: {
  jobCreatedDate: string;
}): boolean => {
  const jobIsoDate = parseISO(jobCreatedDate);

  const threeDaysAfter = add(jobIsoDate, { days: 3 });

  return Boolean(isBefore(Date.now(), threeDaysAfter));
};
interface FreemiumUrgentHireModalProps {
  link: string;
  jobCreatedDate: string;
  trackButtonPressed: () => void;
  trackModalDisplayed: () => void;
  trackModalDismissed: () => void;
}

export const FreemiumUrgentHireModal = ({
  link,
  jobCreatedDate,
  trackModalDisplayed,
  trackButtonPressed,
  trackModalDismissed,
}: FreemiumUrgentHireModalProps) => {
  const { t } = useTranslations(translations);
  const { getFreemiumUrgentHireModalStatus, hideFreemiumUrgentHireModal } =
    useUserActionStatus('freemium-urgent-hire');

  const showModal =
    shouldShowUrgentHire({ jobCreatedDate }) &&
    getFreemiumUrgentHireModalStatus() === 'visible';

  if (!showModal) {
    return null;
  }

  const closeModal = () => {
    trackModalDismissed();
    hideFreemiumUrgentHireModal();
  };
  const titleId = 'FreemiumUrgentHireModal';
  return (
    <TrackOnMount onMount={trackModalDisplayed}>
      <FreemiumModal
        isOpen={showModal}
        close={closeModal}
        titleId={titleId}
        content={
          <FreemiumModalContent
            link={link}
            upgradeTrackingFn={trackButtonPressed}
            titleId={titleId}
            heading={t('Need to hire quickly?')}
            subheading={t(
              'Upgrade anytime to a Basic, Branded or Premium ad for:',
            )}
            dotpoints={[
              {
                description: t(
                  'Basic, Branded and Premium ads usually attract more applications than Lite ads.',
                ),
                heading: t('More candidates faster:'),
              },
              {
                description: t(
                  'Keeps your ad active for an additional 30 days, giving you more time to attract the right candidates.',
                ),
                heading: t('Extend your ad for 30 days:'),
              },
              {
                description: t(
                  'Get a curated list of candidates whose skills and experience match your job ad.',
                ),
                heading: t('Access to Recommended Candidates:'),
              },
            ]}
          />
        }
      />
    </TrackOnMount>
  );
};
